import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { useSiteContext } from "../components/layout/Layout";
import { Download, Search, ChevronDown, ChevronRight, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function AuditLog() {
  const { user } = useAuth();
  const { sites } = useSiteContext();
  const [rows, setRows] = useState([]);
  const [actionFilter, setActionFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [actor, setActor] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    const params = {};
    if (actionFilter !== "all") params.action = actionFilter;
    if (siteFilter !== "all") params.site_id = siteFilter;
    if (actor) params.actor = actor;
    if (start) params.start = new Date(start).toISOString();
    if (end) params.end = new Date(end).toISOString();
    api.get("/audit-log", { params }).then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [actionFilter, siteFilter, actor, start, end]);

  const exportCsv = async () => {
    if (!rows.length) { (await import("sonner")).toast.error("No rows to export"); return; }
    const XLSX = await import("xlsx");
    const sheet = XLSX.utils.json_to_sheet(rows.map((r) => ({
      Timestamp: r.ts ? new Date(r.ts).toLocaleString("en-IN") : "",
      Actor: r.user_email || r.actor || "",
      Action: r.action || "",
      Target: r.target || "",
      Site: r.site_id || "",
      IP: r.ip || "",
    })));
    const csv = XLSX.utils.sheet_to_csv(sheet);
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Fire360Pro_AuditLog_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const ACTIONS = ["all", "auth.login", "site.created", "rule.created", "rule.deleted", "user.invited", "subscription.upgraded", "floorplan.created", "floorplan.updated", "floorplan.deleted"];

  if (user?.role !== "super_admin" && user?.role !== "admin") {
    return <div className="glass p-8 rounded-lg text-center"><ShieldCheck className="w-10 h-10 mx-auto text-orange-400 mb-3" strokeWidth={2.5} /><p className="text-gray-500">Audit log is admin-only.</p></div>;
  }

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Audit Log</h1>
          <p className="text-sm text-gray-500 mt-1">Append-only compliance trail · all critical actions tracked</p>
        </div>
        <Button onClick={exportCsv} className="f360-gradient border-0" data-testid="export-audit-btn"><Download className="w-4 h-4 mr-1" strokeWidth={2.5} />Export CSV</Button>
      </div>

      <div className="glass p-4 rounded-lg grid grid-cols-1 md:grid-cols-5 gap-3">
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue placeholder="Site" /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All sites</SelectItem>
            {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" /><Input placeholder="Actor email" value={actor} onChange={(e) => setActor(e.target.value)} className="pl-10 bg-gray-50 border-gray-200" /></div>
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-gray-50 border-gray-200" />
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-gray-50 border-gray-200" />
      </div>

      <div className="glass rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent sticky top-0 bg-gray-50">
              <TableHead className="w-10"></TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <React.Fragment key={r.audit_id}>
                <TableRow className={`border-gray-200 ${i % 2 === 1 ? "bg-white/[0.015]" : ""}`} data-testid={`audit-${r.audit_id}`}>
                  <TableCell><button onClick={() => setExpanded(expanded === r.audit_id ? null : r.audit_id)} className="text-gray-500">{expanded === r.audit_id ? <ChevronDown className="w-4 h-4" strokeWidth={2.5} /> : <ChevronRight className="w-4 h-4" strokeWidth={2.5} />}</button></TableCell>
                  <TableCell className="text-xs text-gray-600 whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-IN")}</TableCell>
                  <TableCell className="text-sm">{r.actor_email}</TableCell>
                  <TableCell><Badge className="bg-orange-500/20 text-orange-700 border-0 font-mono text-[10px]">{r.action}</Badge></TableCell>
                  <TableCell className="text-xs text-gray-500">{r.entity_type}: {r.entity_id?.substring(0, 12)}…</TableCell>
                  <TableCell className="text-xs">{sites.find((s) => s.site_id === r.site_id)?.name || "—"}</TableCell>
                  <TableCell className="text-xs text-gray-500 font-mono">{r.ip_address || "—"}</TableCell>
                </TableRow>
                {expanded === r.audit_id && (
                  <TableRow className="border-gray-200">
                    <TableCell colSpan={7} className="bg-black/30 text-xs">
                      <div className="grid md:grid-cols-2 gap-4 p-2">
                        <div><div className="text-gray-500 uppercase tracking-wider text-[10px] mb-1">Old</div><pre className="bg-gray-50 p-2 rounded overflow-auto">{JSON.stringify(r.old_value, null, 2)}</pre></div>
                        <div><div className="text-gray-500 uppercase tracking-wider text-[10px] mb-1">New</div><pre className="bg-gray-50 p-2 rounded overflow-auto">{JSON.stringify(r.new_value, null, 2)}</pre></div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No audit entries.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
